import { useCallback, useContext } from 'react';

import type { AuditLogExportFormat } from '../__generated__/apollo-hooks';
import {
  isAuditLogsDownloadAAuditLogsDownloadError,
  isAuditLogsDownloadAAuditLogsDownloadFinished,
  useAuditLogsDownloadStatusLazyQuery,
  useDownloadAuditLogsMutation
} from '../__generated__/apollo-hooks';
import { ERROR_MESSAGES } from '../constants/error-messages';
import type { AuditLogDownloadSource } from '../contexts/downloads.context';
import { DownloadsContext, ManifestKind } from '../contexts/downloads.context';
import {
  logAndThrow,
  pollUntilDeadline
} from '../helpers/download-manifest.helper';
import { getDetailedErrorMessage } from '../helpers/graphql-error.helper';

const toPlatformStartOfDay = (date: Date): Date =>
  new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
  );

const toPlatformEndOfDay = (date: Date): Date =>
  new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      23,
      59,
      59,
      999
    )
  );

type DownloadAuditLogParams = {
  dateRange: [Date, Date];
  format: AuditLogExportFormat;
};

type UseAuditLogDownloadReturn = {
  downloadAuditLog: (params: DownloadAuditLogParams) => void;
};

/**
 * Hook for downloading audit logs as CSV or CEF.
 * Integrates with DownloadsContext to show progress in the download manager.
 *
 * This only describes the job; {@link useResolveAuditLogDownloadUrl} starts and
 * polls it. The bounds are stored as ISO strings because a `Date` does not
 * round-trip through the manifest's `JSON.stringify`/`JSON.parse`.
 */
export const useAuditLogDownload = (): UseAuditLogDownloadReturn => {
  const { manageDownload } = useContext(DownloadsContext);

  const downloadAuditLog = useCallback(
    ({ dateRange, format }: DownloadAuditLogParams) => {
      const id = `audit-log-${Date.now()}`;
      const extension = format === 'CSV' ? 'csv' : 'cef';
      const fileName = `audit-logs.${extension}`;
      const since = dateRange.at(0);
      const until = dateRange.at(1);

      manageDownload({
        id,
        fileName,
        kind: ManifestKind.AUTHENTICATED,
        source: {
          type: 'AUDIT_LOG',
          format,
          since: since ? toPlatformStartOfDay(since).toISOString() : undefined,
          until: until ? toPlatformEndOfDay(until).toISOString() : undefined
        }
      });
    },
    [manageDownload]
  );

  return { downloadAuditLog };
};

type ResolveAuditLogUrl = (
  source: AuditLogDownloadSource,
  startTimestamp: number,
  onSourceResolved: (source: AuditLogDownloadSource) => void,
  signal?: AbortSignal
) => Promise<string>;

/**
 * Starts an audit log export, or resumes one the server is already running:
 *
 * 1. With no `downloadId`, call the downloadAuditLogs mutation and report the
 *    id it returns so the job can be resumed after a reload
 * 2. Poll auditLogsDownloadStatus until Finished or Error
 * 3. Resolve with the downloadUrl from the Finished state
 */
export const useResolveAuditLogDownloadUrl = (): ResolveAuditLogUrl => {
  const [downloadMutation] = useDownloadAuditLogsMutation();
  const [pollStatus] = useAuditLogsDownloadStatusLazyQuery({
    fetchPolicy: 'network-only'
  });

  return useCallback(
    async (source, startTimestamp, onSourceResolved, signal) => {
      let { downloadId } = source;

      if (!downloadId) {
        const response = await downloadMutation({
          variables: {
            format: source.format,
            since: source.since ? new Date(source.since) : undefined,
            until: source.until ? new Date(source.until) : undefined
          }
        });

        const downloadResult = response.data?.downloadAuditLogs;

        if (!downloadResult) {
          return logAndThrow(ERROR_MESSAGES.downloads.initiateFailed.title);
        }

        if (isAuditLogsDownloadAAuditLogsDownloadFinished(downloadResult)) {
          return downloadResult.downloadUrl;
        }

        if (isAuditLogsDownloadAAuditLogsDownloadError(downloadResult)) {
          return logAndThrow(
            downloadResult.message ?? ERROR_MESSAGES.downloads.failed.title
          );
        }

        downloadId = downloadResult.id;
        onSourceResolved({ ...source, downloadId });
      }

      return pollUntilDeadline(
        startTimestamp,
        async () => {
          const { data: statusData, error } = await pollStatus({
            variables: { downloadId }
          });

          if (error) {
            return logAndThrow(getDetailedErrorMessage(error));
          }

          const node = statusData?.auditLogsDownloads?.edges?.at(0)?.node;

          if (!node) {
            return logAndThrow(ERROR_MESSAGES.downloads.jobNotFound.title);
          }

          if (isAuditLogsDownloadAAuditLogsDownloadFinished(node)) {
            return node.downloadUrl;
          }

          if (isAuditLogsDownloadAAuditLogsDownloadError(node)) {
            return logAndThrow(
              node.message ?? ERROR_MESSAGES.downloads.failed.title
            );
          }

          return null;
        },
        signal
      );
    },
    [downloadMutation, pollStatus]
  );
};
